import React, { useEffect, useState } from 'react'
import { ActivityIndicator, Image, Linking, StyleSheet } from 'react-native'

import { publishEvent } from '@zet/analytics'
import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Stack, XStack } from '@zet/design-system/layout'
import CalenderOutlined from '@zet/design-system/svg/CalenderOutlined'
import ClockOutlined from '@zet/design-system/svg/ClockOutlined'
import { B4, B5, BTN_SM, SH1, Text } from '@zet/design-system/typography'
import { i18n } from '@zet/localization'
import LockFilled from '@zet/svg/lockFilled'
import { UpcomingTrainingItem } from '@zet/utils/interface'
import moment from 'moment'

import { useEnrollToTraining } from '../../../api/trainings/hooks/useEnrollToTraining'

const TrainingCard = ({ item }: { item: UpcomingTrainingItem }) => {
  const { isLoading, mutate } = useEnrollToTraining()
  const [isReferesh, setIsReferesh] = useState(false)
  const {
    imageURI,
    title,
    isEnrolled,
    id,
    startTime,
    time,
    status,
    language,
    isBlocked,
  } = item
  const hanldeButtonClick = () => {
    if (isEnrolled) {
      Linking.openURL(item.link || 'https://onecode.in')
      publishEvent('click_action', { item_name: 'trainings_join_now' })
    } else {
      mutate({
        webinarId: id,
      })
      publishEvent('click_action', { item_name: 'trainings_enroll_now' })
    }
  }

  useEffect(() => {
    const refereshInterval = setInterval(() => {
      setIsReferesh(!isReferesh)
    }, 1000 * 30)
    return () => clearInterval(refereshInterval)
  }, [isReferesh])

  const hourMinute = (unxiTime: number) => {
    const resolution = unxiTime - moment(new Date()).unix()
    return {
      hours: Number((resolution / 3600).toFixed(1)),
      minutes: Number((resolution / 60).toFixed(0)),
    }
  }

  const getButtonTitle = (status: string, startTime: number, title: string) => {
    const { hours, minutes } = hourMinute(startTime)

    if (status === 'REGISTERED') {
      if (minutes > 60)
        return i18n.t('trainingCard.startsIn', {
          hours: hours.toFixed(0),
        })
      else if (minutes > 15)
        return i18n.t('trainingCard.startsInMins', { minutes: minutes })
      else return i18n.t('trainingCard.joinNow')
    } else return i18n.t('trainingCard.enrollNow')
  }

  const isLive = (startTime: number) => {
    const { minutes } = hourMinute(startTime)
    return minutes > 15 ? false : true
  }

  const getSubtitle = (startTime: number) => {
    const getStartTimeFormat = moment.unix(startTime)
    return `${getStartTimeFormat.format('Do')} ${getStartTimeFormat.format(
      'MMM'
    )}, ${getStartTimeFormat.format('ddd')} `
  }

  const getStartAndEndTime = (startTime: number, endTime: number) => {
    const getStartTimeFormat = moment.unix(startTime)
    const getEndTimeFormat = moment.unix(endTime)

    return `${getStartTimeFormat.format('h:mm A')} - ${getEndTimeFormat.format(
      'hh:mm A'
    )}`
  }

  const isTrainingLive = status === 'UNREGISTERED' ? false : isLive(startTime)
  const date = getSubtitle(startTime)
  const buttonTitle = getButtonTitle(status, startTime, title)

  return (
    <Stack className="bg-neutral-white100 mt-4 px-3 pb-4">
      <Stack>
        <Image
          source={
            imageURI
              ? { uri: imageURI }
              : require('../../../assets/images/training-placeholder.png')
          }
          className="rounded"
          style={styles.imageStyle}
        />
        <Stack className="absolute right-0 top-0 rounded-bl bg-[#1A0B0B] px-2 pb-[2]">
          <Text className="text-xs text-neutral-100">{language}</Text>
        </Stack>
      </Stack>
      <SH1 className="py-4/2">{title}</SH1>
      <XStack className="justify-between">
        <Stack>
          <XStack>
            <ClockOutlined size={18} />
            <B5 className="ml-1">{date}</B5>
          </XStack>
          <XStack className="mt-4/2">
            <CalenderOutlined size={20} />
            <B5 className="ml-1">{time}</B5>
          </XStack>
        </Stack>
        {isBlocked ? (
          <Stack
            className="absolute right-[15] top-[5] h-6 w-6 items-center justify-center rounded border"
            style={{ backgroundColor: zetColors.tertiary[100] }}
          >
            <LockFilled />
          </Stack>
        ) : isEnrolled && buttonTitle !== i18n.t('trainingCard.joinNow') ? (
          <B4 className="mt-3">{buttonTitle}</B4>
        ) : (
          <Button
            intent={isTrainingLive ? 'success' : 'primary'}
            className={`justify-center ${
              isTrainingLive
                ? 'border-success-100 active:bg-success-100 active:border-white'
                : 'border-primary-100 active:bg-primary-100 active:border-white'
            }`}
            onPress={() => hanldeButtonClick(id)}
            style={styles.buttonWidth}
            iconLeft={
              isLoading && (
                <ActivityIndicator
                  size={'small'}
                  color={zetColors.neutral.white100}
                />
              )
            }
            disabled={isLoading}
          >
            <BTN_SM>{buttonTitle}</BTN_SM>
          </Button>
        )}
      </XStack>
    </Stack>
  )
}

export default TrainingCard

const styles = StyleSheet.create({
  imageStyle: {
    height: 120,
  },
  liveDot: {
    width: 10,
    height: 10,
  },
  buttonWidth: {
    maxWidth: 130,
  },
})
